import { fastCompact } from "@/utils/object-utils";

// SAML is technically also an Identity "provider", but we're referring to OIDC/Oauth
export type SAMLLoginProvider = { type: "saml"; team: string; entityId?: string; name?: string };
export type OIDCLoginProvider = { type: "provider"; id: string };
export type LoginProvider = SAMLLoginProvider | OIDCLoginProvider;
export interface LoginSpec {
  team?: string;
  samlTeam?: string;
  emailDomains: string[];
  providers: LoginProvider[];
}
export type OIDCProvider =
  | "github"
  | "azure-ad"
  | "google"
  | "email"
  | "keycloak"
  | "grindr-onelogin";

const INTERNAL_KEYCLOAK_ENABLED = process.env.INTERNAL_KEYCLOAK_CLIENT_SECRET != null;

// staging.chalk.dev saml config
const getAllLoginSpecs = (isKeyCloakEnabled: boolean): LoginSpec[] => [
  {
    samlTeam: "cl1cpe52r000009l0662k3m11",
    emailDomains: ["chalk.ai"],
    providers: fastCompact([
      { type: "saml", team: "cl1cpe52r000009l0662k3m11" },
      isKeyCloakEnabled ? { type: "provider", id: "keycloak-chalk-internal" } : null,
      // Michael's special local testing thing
      // "chalk.ai": { type: "saml", team: "team" },
    ]),
  },
  {
    emailDomains: ["playwright.chalk.dev"],
    providers: fastCompact([
      isKeyCloakEnabled ? { type: "provider", id: "keycloak-chalk-internal" } : null,
    ]),
  },
  {
    samlTeam: "aloyz5du7gkee",
    emailDomains: ["alloy.com"],
    providers: [{ type: "saml", team: "aloyz5du7gkee" }],
  },
  {
    samlTeam: "brxyxkszayzq",
    emailDomains: ["brex.com"],
    providers: [{ type: "saml", team: "brxyxkszayzq" }],
  },
  {
    samlTeam: "crgebcfeqfimk",
    emailDomains: ["creditgenie.com"],
    providers: [{ type: "saml", team: "crgebcfeqfimk" }],
  },
  {
    samlTeam: "fndjfdr5f9tr",
    emailDomains: ["found.com"],
    providers: [{ type: "saml", team: "fndjfdr5f9tr" }],
  },
  {
    team: "gndr66seykrpc",
    samlTeam: "gndr66seykrpc",
    emailDomains: ["grindr.com", "team.grindr.com"],
    providers: fastCompact([
      { type: "saml", team: "gndr66seykrpc" },
      process.env["GRINDR_ONELOGIN_CLIENT_SECRET"]
        ? { type: "provider", id: "grindr-onelogin" }
        : null,
    ]),
  },
  {
    samlTeam: "melio",
    emailDomains: ["melio.com"],
    providers: [{ type: "saml", team: "melio" }],
  },
  {
    samlTeam: "mcys9wgcm34g",
    emailDomains: ["mercury.com"],
    providers: [{ type: "saml", team: "mcys9wgcm34g" }],
  },
  {
    samlTeam: "moni6igx3kit",
    emailDomains: ["gendigital.com"],
    providers: [
      {
        type: "saml",
        team: "moni6igx3kit",
        name: "azure-ad",
        entityId: "https://sts.windows.net/94986b1d-466f-4fc0-ab4b-5c725603deab/",
      },
    ],
  },
  {
    samlTeam: "misl89g8a99yc",
    emailDomains: ["missionlane.com"],
    providers: [{ type: "saml", team: "misl89g8a99yc" }],
  },
  {
    samlTeam: "moni6igx3kit",
    emailDomains: ["moneylion.com"],
    providers: [
      {
        type: "saml",
        team: "moni6igx3kit",
        name: "okta",
        entityId: "http://www.okta.com/exkg9jzeqfpr81HlV697",
      },
      {
        type: "saml",
        team: "moni6igx3kit",
        name: "azure-ad",
        entityId: "https://sts.windows.net/94986b1d-466f-4fc0-ab4b-5c725603deab/",
      },
    ],
  },
  {
    samlTeam: "mfprfuwt5izd",
    emailDomains: ["myfitnesspal.com"],
    providers: [{ type: "saml", team: "mfprfuwt5izd" }],
  },
  {
    samlTeam: "prsnt7izk8syf",
    emailDomains: ["withpersona.com"],
    providers: [{ type: "saml", team: "prsnt7izk8syf" }],
  },
  {
    samlTeam: "rmp",
    emailDomains: ["ramp.com"],
    providers: [{ type: "saml", team: "rmp" }],
  },
  {
    samlTeam: "sce3dp9qa3mb",
    emailDomains: ["socure.com", "socure.me"],
    providers: [{ type: "saml", team: "sce3dp9qa3mb" }],
  },
  {
    samlTeam: "whtntbt5tu58pe",
    emailDomains: ["whatnot.com"],
    providers: [{ type: "saml", team: "whtntbt5tu58pe" }],
  },
];

/**
 * A mapping of email domains to the information necessary to send the user
 * to the next step of authentication
 * (e.g. the right oidc client, the team used to query for the right SAML configuration)
 */
const getLoginSpecsByEmailDomain = (isKeycloakEnabled: boolean): { [index: string]: LoginSpec } =>
  Object.fromEntries(
    getAllLoginSpecs(isKeycloakEnabled).flatMap((spec) =>
      spec.emailDomains.map((email) => [email, spec]),
    ),
  );

export function getHardcodedLoginSpecs(isKeycloakEnabled = INTERNAL_KEYCLOAK_ENABLED): LoginSpec[] {
  return getAllLoginSpecs(isKeycloakEnabled);
}

export const ASSOCIATED_TEAMS_FOR_PROVIDER: Partial<Record<string, string>> = Object.fromEntries(
  getAllLoginSpecs(INTERNAL_KEYCLOAK_ENABLED).flatMap((spec) =>
    spec.providers
      .filter(
        (provider): provider is OIDCLoginProvider =>
          provider.type === "provider" && spec.team != null,
      )
      .map((provider) => [provider.id, spec.team!]),
  ),
);

const onPremiseEmails = new Set([
  "iwoca.com",
  "socure.me",
  "socure.com",
  "melio.com",
  "grindr.com",
]);

// FIXME: this is terrible - need to special case this email so that
// ddog can login to prod with an email domain that is normally mapped to staging only
export const PROD_CHALK_CUSTOM_SSO_PROVIDER_MAPPING: { [index: string]: LoginProvider } = {
  "ddog-login-tester@chalk.ai": {
    type: "saml",
    team: "1",
  },
  "ddog-login-tester-staging@chalk.ai": {
    type: "saml",
    team: "plgd3tu89qeu",
  },
};

export function getHardcodedProvidersForEmail(
  email: string,
  isSessionLogin?: boolean,
  currentFrontendHost?: string,
  // This env var is only filled in server-side - ui must pass this in explicitly
  // due to how env vars are baked in at build time, this is unreliable
  isKeyCloakEnabled = INTERNAL_KEYCLOAK_ENABLED,
): { type: "success"; providers: LoginProvider[] } | { type: "error"; error: string } {
  // special case email addresses
  if (email in PROD_CHALK_CUSTOM_SSO_PROVIDER_MAPPING) {
    const provider = PROD_CHALK_CUSTOM_SSO_PROVIDER_MAPPING[email];
    return { type: "success", providers: [provider] };
  }

  try {
    const split = email.split("@");
    const trimmed = split[1].trim().toLowerCase();
    if (onPremiseEmails.has(trimmed) && currentFrontendHost === "chalk.ai") {
      return {
        type: "error",
        error: `Your organization uses an isolated deployment. ${isSessionLogin ? " Please authenticate your terminal session using `chalk login --api-host=...` with the custom API host for your organization`" : " Please login at the custom web URL provided for this organization."}`,
      };
    }

    const loginSpec = getLoginSpecsByEmailDomain(isKeyCloakEnabled)[trimmed];
    if (!loginSpec) {
      return { type: "error", error: "Your organization does not have SSO configured." };
    } else {
      return { type: "success", providers: loginSpec.providers };
    }
  } catch {
    return { type: "error", error: "Invalid email address." };
  }
}
